
import {defineComponent, PropType} from "vue";
import SmallHeaderComponent from "@/components/recorder/partials/SmallHeaderComponent.vue";
import {Campaign} from "@/models/campaign";
import FooterComponent from "@/components/recorder/partials/FooterComponent.vue";
import {Recorder} from "@/utils/recorder";
import {Contribution} from "@/models/contribution";
import {Recording} from "@/models/recording";
import {mapActions} from "pinia";
import {useAnalyticsStore} from "@/store/analytics.store";
import {StreamTranscriptionClient, TranscriptionStreamResult} from "@/utils/streamTranscriptionClient";
import {CreateTranscribeStreamUrlMutation, useCreateTranscribeStreamUrlMutation} from "@/graphql/API";
import {FetchResult} from "@apollo/client";
import {provideApolloClient} from "@vue/apollo-composable";
import {apolloClient} from "@/providers/graphql.provider";

enum RecordState {
    Recording = "recording",
    Stopped = "stopped",
    Playing = "playing",
    Countdown = "countdown",
}

interface Playback {
    duration: number;
    player: HTMLAudioElement;
}

interface RecordComponentData {
    state: RecordState
    timer: number,
    maximumRecordingDuration: number;
    recording: Recording;
    playback: Playback;
    mediaRecorder?: MediaRecorder;
    recordedChunks?: Blob[];
    recordingTimer?: number;
    followUpQuestion?: string;
    streamTranscriptionClient: StreamTranscriptionClient;
    transcript: string;
    countdownTimerInterval: number | undefined;
}

export default defineComponent({
    name: "RecordComponent",
    components: {FooterComponent, SmallHeaderComponent},
    props: {
        campaign: { type: Object as PropType<Campaign>, required: true },
        recorder: { type: Object as PropType<Recorder>, required: true },
        contribution: { type: Object as PropType<Contribution>, required: true },
        conversationRoundIndex: { type: Number, required: true },
        promptText: { type: String, required: true },
    },
    data(): RecordComponentData {
        return {
            state: RecordState.Countdown,
            timer: 3,
            maximumRecordingDuration: 90,
            recording: {
                duration: 0,
                blob: new Blob(),
            },
            playback: {
                duration: 0,
                player: new Audio(),
            },
            mediaRecorder: undefined,
            recordedChunks: undefined,
            recordingTimer: undefined,
            streamTranscriptionClient: new StreamTranscriptionClient({
                transcribeStreamURLFetcher: () => {
                    return this.createTranscribeStreamURL();
                },
            }),
            transcript: "",
            countdownTimerInterval: undefined,
        };
    },
    mounted(): void {
        provideApolloClient(apolloClient);

        if (this.campaign.maximumRecordingDuration) {
            this.maximumRecordingDuration = this.campaign.maximumRecordingDuration;
        }

        if (this.contribution.recordings[this.conversationRoundIndex]) {
            this.state = RecordState.Stopped;
            this.recording = this.contribution.recordings[this.conversationRoundIndex];
            this.transcript = this.contribution.conversation[this.conversationRoundIndex].transcript || "";
            this.loadRecordingIntoPlayer();
        } else {
            this.countdownTimerInterval = setInterval(async () => {
                this.timer--;
                if (this.timer === 0) {
                    this.state = RecordState.Recording;
                    clearInterval(this.countdownTimerInterval);
                    this.initializeNewRecorder();
                    this.startRecording();
                }
            }, 1000);
        }
    },
    computed: {
        canContinue() {
            return this.recording.blob.size > 0;
        },

        recordingProgress() {
            return (this.recording.duration / this.maximumRecordingDuration) * 100;
        },

        recordingTimeLeft() {
            return new Date((this.maximumRecordingDuration - this.recording.duration) * 1000).toISOString().substr(14, 5);
        },

        playbackProgress() {
            return Math.round((this.playback.duration / this.recording.duration) * 100);
        },

        playbackTimeLeft() {
            return new Date((this.recording.duration - this.playback.duration) * 1000).toISOString().substr(14, 5);
        },
    },
    methods: {
        // analytics store
        ...mapActions(useAnalyticsStore, ['trackEvent']),

        // RECORDING
        initializeNewRecorder(): void {
            try {
                this.mediaRecorder = this.makeMediaRecorder(this.recorder.getStream());
            } catch (e) {
                console.error(e);
            }
        },
        makeMediaRecorder(stream: MediaStream | undefined): MediaRecorder {
            if (!stream) {
                throw new Error('No stream available');
            }

            const mediaRecorder: MediaRecorder = new MediaRecorder(stream, { mimeType: this.recorder.firstSupportedMediaType() });
            mediaRecorder.addEventListener('dataavailable', this.mediaRecorderDataAvailableEventHandler);
            mediaRecorder.addEventListener('stop', this.mediaRecorderStopEventHandler);

            mediaRecorder.onerror = (error) => {
                console.error('Error reported by the MediaRecorder');
                console.error(error);
            };

            if (this.$route.query['ff_conversational_survey']) {
                this.transcribeStream(stream)
            }

            return mediaRecorder;
        },
        mediaRecorderDataAvailableEventHandler(event: BlobEvent): void {
            if (event.data.size > 0) {
                this.recordedChunks?.push(event.data);
            }
        },
        mediaRecorderStopEventHandler(): void {
            if (this.recordedChunks && this.recordedChunks.length > 0) {
                this.recording.blob = new Blob(this.recordedChunks, { type: this.recorder.firstSupportedMediaType() });
                this.loadRecordingIntoPlayer();
                this.$emit('recorderDataUpdated', { recording: this.recording, conversationRoundIndex: this.conversationRoundIndex, transcript: this.transcript });
                this.stopStreamAndRemoveListeners();
            } else {
                this.reset();
            }
        },
        stopStreamAndRemoveListeners() {
            this.mediaRecorder?.removeEventListener('dataavailable', this.mediaRecorderDataAvailableEventHandler);
            this.mediaRecorder?.removeEventListener('stop', this.mediaRecorderStopEventHandler);
            this.mediaRecorder = undefined;
            this.recorder.destroyStream();
        },
        startRecording(): void {
            if (this.mediaRecorder === undefined) {
                return;
            }

            if (this.mediaRecorder.stream.getAudioTracks().length < 1) {
                this.stopRecording();
                this.deleteAudio();
                alert(this.$t('creation.anotherAppRecording'));
            } else if (this.mediaRecorder.stream.getAudioTracks().length > 0 && this.mediaRecorder.stream.getAudioTracks()[0].muted) {
                this.stopRecording();
                this.deleteAudio();
                alert(this.$t('creation.microphoneErrors.mutedMicrophoneError'));
            } else{
                this.recordedChunks = [];
                this.state = RecordState.Recording;
                this.startRecordingTimer();
                this.mediaRecorder.start();
            }
        },
        stopRecording(): void {
            if (this.mediaRecorder === undefined) {
                return;
            }

            if (this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }

            if (this.recordingTimer) {
                clearInterval(this.recordingTimer);
                this.recordingTimer = undefined;
            }
            this.state = RecordState.Stopped;
        },
        startRecordingTimer(): void {
            this.recordingTimer = window.setInterval(() => {
                this.recording.duration += 0.01;
                if (this.recording.duration >= this.maximumRecordingDuration) {
                    this.stopRecording();
                }
            }, 10);
        },

        // PLAYBACK
        async loadRecordingIntoPlayer(): Promise<void> {
            const src = URL.createObjectURL(this.recording.blob);
            this.playback.player = new Audio(src);
            this.playback.player.addEventListener('ended', this.handlePlaybackFinished);
            this.playback.player.addEventListener('timeupdate', this.updatePlaybackCurrentTime);
        },
        handlePlaybackFinished(): void {
            this.pauseAudio();
            this.playback.duration = 0;
        },
        updatePlaybackCurrentTime(): void {
            this.playback.duration = this.playback.player.currentTime;
        },
        pauseAudio(): void {
            this.playback.player.pause();
            this.state = RecordState.Stopped;
        },

        // GENERAL
        deleteAudio(): void {
            this.trackEvent('creationAudioRecordingDeleteTapped', {});
            this.reset();
            this.goToPromptScreen();
        },
        async playAudio(): Promise<void> {
            this.trackEvent('creationAudioRecordingPlayTapped', {});
            try {
                await this.playback.player.play();
                this.state = RecordState.Playing;
            } catch (e) {
                console.error(e);
            }
        },

        reset() {
            if (this.countdownTimerInterval) {
                clearInterval(this.countdownTimerInterval);
            }

            // causing stop would emit the recordings to the listeners, we don't want this with reset.
            this.pauseAudio();
            this.stopStreamAndRemoveListeners();
            this.state = RecordState.Countdown;

            if (this.playback.player) {
                this.playback.player.removeEventListener('ended', this.handlePlaybackFinished);
                this.playback.player.removeEventListener('timeupdate', this.updatePlaybackCurrentTime);
            }

            this.transcript = "";
            this.recording = {
                duration: 0,
                blob: new Blob(),
            };
            this.playback = {
                player: new Audio(),
                duration: 0,
            };

            this.$emit('recorderDataUpdated', { recording: undefined, transcript: "", conversationRoundIndex: this.conversationRoundIndex });
        },

        goToPromptScreen() {
            this.reset();
            this.$emit('goToPromptScreen');
        },

        continueToNextScreen() {
            if (this.canContinue) {
                this.pauseAudio();
                this.$emit('goToToNextScreen');
            }
        },
        transcribeStream(rawStream: MediaStream) {
            this.streamTranscriptionClient.registerStream(rawStream);
            this.streamTranscriptionClient.iterator().then((readableStream) => {
                return this.subscribeToTranscriptStream(readableStream);
            });
        },

        async subscribeToTranscriptStream(stream: AsyncIterable<TranscriptionStreamResult>) {
            for await (const { transcriptText, isPartial } of stream) {
                if (isPartial) {
                    continue;
                }

                this.transcript += " " + transcriptText;
                this.$emit('recorderDataUpdated', { recording: this.recording, transcript: this.transcript, conversationRoundIndex: this.conversationRoundIndex });
            }
        },

        async createTranscribeStreamURL(): Promise<string> {
            const { mutate } = useCreateTranscribeStreamUrlMutation({
                variables: {
                    campaignID: this.campaign.id,
                },
            });

            const mutationResponse: FetchResult<CreateTranscribeStreamUrlMutation> | null = await mutate();
            if (!mutationResponse || !mutationResponse.data) {
                throw new Error("Failed to create transcribe stream URL");
            }

            return mutationResponse.data.createTranscribeStreamURL.url;
        }
    }
});
